import React from 'react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, ReferenceLine
} from 'recharts';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

const GROWTH_DOMAIN_CAP = 100;

const CustomTooltipGrowth = ({ active, payload, label, growthCap = GROWTH_DOMAIN_CAP }) => {
  if (!active || !payload || !payload.length) return null;

  const lineEntry = payload.find(entry => entry.stroke && entry.dataKey === 'crescimento');
  if (!lineEntry) return null;

  const valor = parseFloat(lineEntry.value);
  const truncado = Math.abs(valor) > growthCap;

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: 8,
      fontSize: 12,
      color: 'var(--text-primary)',
      boxShadow: 'var(--shadow)'
    }}>
      <div style={{ fontWeight: 'bold', marginBottom: 4, color: 'var(--text-primary)' }}>
        {formatMes(label)}
      </div>
      <div style={{ color: lineEntry.stroke }}>
        {valor.toFixed(2)}%
      </div>
      {truncado && (
        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>
          Valor exibido na escala: {valor > 0 ? growthCap : -growthCap}%
        </div>
      )}
    </div>
  );
};

// Constante de meses (evita recriar a cada render)
const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

// Funções auxiliares para formatação
const formatMes = (value) => {
  if (!value) {
    return '';
  }
  
  // Se já é um mês formatado (ex: "Jan", "Fev"), retornar como está
  if (MESES.includes(value)) {
    return value;
  }
  
  // Se tem formato YYYY-MM
  if (value.includes('-')) {
    const [ano, mes] = value.split('-');
    const mesIndex = parseInt(mes, 10) - 1;
    
    if (mesIndex >= 0 && mesIndex < 12) {
      return `${MESES[mesIndex]}/${ano.slice(2)}`;
    } else {
      return value;
    }
  }
  
  // Se não tem formato conhecido, retornar como está
  return value;
};

const formatValor = (value) => {
  if (value >= 1000000) return `R$ ${(value/1000000).toFixed(1)}M`;
  return `R$ ${(value/1000).toFixed(0)}k`;
};

const formatTitulos = (value) => {
  if (value >= 1000000) return `${(value/1000000).toFixed(1)}M`;
  return `${(value/1000).toFixed(0)}k`;
};

// Dots customizados com key
const CustomDot = (props) => {
  const { cx, cy, payload, key: reactKey } = props;
  const valor = payload.crescimento;
  const cor = valor >= 0 ? '#10b981' : '#ef4444';
  return (
    <circle
      key={reactKey}
      cx={cx}
      cy={cy}
      r={6}
      fill={cor}
      stroke="#fff"
      strokeWidth={3}
    />
  );
};

const CustomActiveDot = (props) => {
  const { cx, cy, payload, key: reactKey } = props;
  const valor = payload.crescimento;
  const cor = valor >= 0 ? '#10b981' : '#ef4444';
  return (
    <circle
      key={reactKey}
      cx={cx}
      cy={cy}
      r={8}
      fill={cor}
      stroke="#fff"
      strokeWidth={3}
    />
  );
};

const GraficoEvolucaoMensal = ({ dadosGrafico, _insights, loading = false, accentColor = '#3b82f6' }) => {
  const [visualizacao, setVisualizacao] = React.useState('geral'); // 'geral' ou 'crescimento'
  const term = useReceivableTerm();

  // Processar dados para o formato esperado pelo gráfico
  const dadosProcessados = React.useMemo(() => {
    if (!dadosGrafico || !Array.isArray(dadosGrafico) || dadosGrafico.length === 0) {
      return [];
    }

    return dadosGrafico.map((item, index) => {
      // Usar os campos corretos do dashboardCompletoService
      const valorRecebido = item.valorRecebido || item.receita_total || 0;
      const titulosRecebidos = item.titulosRecebidos || item.total_contratos || 0;
      
      // Calcular crescimento em relação ao mês anterior
      let crescimento = 0;
      if (index > 0) {
        const valorAnterior = dadosGrafico[index - 1].valorRecebido || dadosGrafico[index - 1].receita_total || 0;
        if (valorAnterior > 0) {
          crescimento = ((valorRecebido - valorAnterior) / valorAnterior) * 100;
        }
      }

      return {
        mes: item.mes || `Mês ${index + 1}`,
        valorRecebido,
        titulosRecebidos,
        crescimento: parseFloat(crescimento.toFixed(2))
      };
    });
  }, [dadosGrafico]);

  // Domain do eixo Y do crescimento: limitar a ±CAP para não distorcer a escala com outliers
  const growthDomain = React.useMemo(() => {
    if (!dadosProcessados || dadosProcessados.length === 0) return [-GROWTH_DOMAIN_CAP, GROWTH_DOMAIN_CAP];
    const valores = dadosProcessados.map(d => d.crescimento).filter(v => typeof v === 'number' && !Number.isNaN(v));
    if (valores.length === 0) return [-GROWTH_DOMAIN_CAP, GROWTH_DOMAIN_CAP];
    const minCrescimento = Math.min(...valores);
    const maxCrescimento = Math.max(...valores);
    const minDomain = Math.max(minCrescimento, -GROWTH_DOMAIN_CAP);
    const maxDomain = Math.min(maxCrescimento, GROWTH_DOMAIN_CAP);
    if (minDomain === maxDomain) {
      return [Math.max(-GROWTH_DOMAIN_CAP, minDomain - 10), Math.min(GROWTH_DOMAIN_CAP, maxDomain + 10)];
    }
    return [minDomain, maxDomain];
  }, [dadosProcessados]);

  if (loading) {
    return (
      <div className="p-4 h-full min-h-[420px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur animate-pulse">
        <div className="h-3 w-24 bg-[color:var(--surface-muted)] rounded mb-3"></div>
        <div className="h-5 w-48 bg-[color:var(--surface-muted)] rounded mb-2"></div>
        <div className="h-3 w-64 bg-[color:var(--surface-muted)] rounded mb-6"></div>
        <div className="h-56 w-full bg-[color:var(--surface-muted)] rounded"></div>
      </div>
    );
  }

  if (!dadosProcessados || dadosProcessados.length === 0) {
    return (
      <div className="p-6">
        <div className="text-center text-[color:var(--text-muted)]">
          <div className="w-16 h-16 mx-auto mb-4">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium mb-2 text-[color:var(--text-primary)]">Dados do Gráfico</h3>
          <p>Carregando dados de evolução mensal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 h-full min-h-[420px] rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur transition-colors duration-200 hover:bg-[color:var(--surface-muted)]">
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-left">
          <div className="text-[11px] font-semibold tracking-[0.35em] text-[color:var(--text-muted)] uppercase">
            Evolucao
          </div>
          <h2 className="text-base font-semibold text-[color:var(--text-primary)] mt-1 tracking-tight">Recebimentos por Mes</h2>
          <p className="text-xs text-[color:var(--text-muted)] mt-1">Valores e {term.pluralLower} com leitura de crescimento</p>
        </div>

        {/* Botões de alternância */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setVisualizacao('geral')}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
              visualizacao === 'geral'
                ? 'text-white'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'
            }`}
            style={visualizacao === 'geral' ? { background: accentColor } : undefined}
          >
            Visão Geral
          </button>
          <button
            onClick={() => setVisualizacao('crescimento')}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
              visualizacao === 'crescimento'
                ? 'text-white'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-muted)]'
            }`}
            style={visualizacao === 'crescimento' ? { background: accentColor } : undefined}
          >
            Foco Crescimento
          </button>
        </div>
      </div>

      <div className="h-64 lg:h-72 mb-2 flex-1">
        <ResponsiveContainer width="100%" height="100%">
          {visualizacao === 'geral' ? (
            <ComposedChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 35 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" />
              <XAxis 
                dataKey="mes" 
                stroke="#94a3b8"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                tickFormatter={formatMes}
              />
              <YAxis 
                yAxisId="left"
                stroke="#60a5fa"
                fontSize={9}
                tickFormatter={formatValor}
                label={{ value: 'Valores', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fontSize: 9, fill: '#94a3b8' } }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="#34d399"
                fontSize={9}
                tickFormatter={formatTitulos}
                label={{ value: term.plural, angle: 90, position: 'insideRight', style: { textAnchor: 'middle', fontSize: 9, fill: '#94a3b8' } }}
              />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'Valor Recebido') return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, name];
                  if (name === term.receivedLabel) return [`${value.toLocaleString('pt-BR')} ${term.pluralLower}`, name];
                  return [value, name];
                }}
                labelFormatter={formatMes}
                labelStyle={{ color: 'var(--text-primary)', fontWeight: 'bold' }}
                contentStyle={{
                  backgroundColor: 'var(--surface)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  fontSize: '12px',
                  boxShadow: 'var(--shadow)',
                  color: 'var(--text-primary)'
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
              <Bar
                yAxisId="left"
                dataKey="valorRecebido"
                fill="#3b82f6"
                name="Valor Recebido"
                opacity={0.7}
                radius={[2, 2, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="titulosRecebidos"
                stroke="#34d399"
                strokeWidth={3}
                name={term.receivedLabel}
                dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
              />
            </ComposedChart>
          ) : (
            <ComposedChart
              data={dadosProcessados}
              margin={{ top: 20, right: 30, left: 20, bottom: 40 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.18)" vertical={false} />
              <XAxis 
                dataKey="mes"
                stroke="#94a3b8"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                tickFormatter={formatMes}
                padding={{ left: 15, right: 15 }}
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={10}
                domain={growthDomain}
                tickFormatter={(value) => `${value}%`}
                tickLine={false}
                axisLine={{ stroke: 'rgba(148,163,184,0.35)' }}
                label={{
                  value: 'Crescimento (%)',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    textAnchor: 'middle',
                    fontSize: 10,
                    fontWeight: 'bold',
                    fill: '#4b5563'
                  }
                }}
              />

              <defs>
                <linearGradient id="crescimentoPositivo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="crescimentoNegativo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0.1} />
                </linearGradient>
              </defs>

              {/* Área com gradiente dinâmico */}
              <Area
                fillOpacity={0.5}
                type="monotone"
                dataKey="crescimento"
                stroke="none"
                fill="url(#crescimentoPositivo)"
                activeDot={false}
              />
              {/* Linha de crescimento personalizada */}
              <Line
                name="Crescimento"
                type="monotone"
                dataKey="crescimento"
                stroke="#34d399"
                strokeWidth={3}
                dot={CustomDot}
                activeDot={CustomActiveDot}
              />
              <ReferenceLine
                y={0}
                stroke="#94a3b8"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
                 <Tooltip content={(props) => <CustomTooltipGrowth {...props} growthCap={GROWTH_DOMAIN_CAP} />} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GraficoEvolucaoMensal;


